import React, {useEffect, useMemo, useState} from "react";
import {SelectChangeEvent} from "@mui/material";
import iconDownload from "@source/assets/icons/iconDownload.svg";
import Svg from "@source/components/UI/svg/svg";
import LoaderComponent from "@components/loader/loader";
import MultiSelectInput from "@components/UI/inputs/multiSelectInput/multiSelectInput";
import useFetching from "@source/hooks/useFetching";
import ConsentController from "@source/api/controllers/consentController";
import {
  ConsentCategory,
  ConsentDocument,
  ConsentDocumentDisplayStatus,
  ConsentDocumentQuery,
  ConsentDocumentStatus,
} from "@source/api/models/consentModels";
import {notifyConsentReportError, saveConsentReportFile} from "@source/api/models/consentReport";
import Notification from "@source/components/snackMessage/notification";
import {
  consentDocumentDetailPath,
  consentDocumentEditPath,
  ConsentHistoryFilters,
  ConsentHistoryStatusFilter,
  createConsentDocumentHistoryQuery,
} from "@source/api/models/consentHelpers";
import DateRangePicker from "@source/components/UI/inputs/dateRangePicker/dateRangePicker";
import SearchInput from "@source/components/UI/inputs/searchInput/searchInput";
import SelectInput, {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";
import {IFillVariant} from "@source/utils/enums";
import useIncrementalConsentList from "@source/hooks/useIncrementalConsentList";
import {ConsentDocumentCard} from "./consentDocumentCard";
import {canDownloadConsentReports} from "@source/utils/consentAccess";
import {ConsentAutoLoad} from "./consentAutoLoad";
import {ConsentFilterBar} from "./consentFilterBar";
import {ConsentRequestState} from "./consentRequestState";
import styles from "./consentDocumentHistory.module.scss";

const statusOptions: ISelectItem[] = [
  {id: "all", value: "all", title: "All statuses"},
  {id: "draft", value: "lifecycle:draft", title: "Draft"},
  {id: "awaiting-signatures", value: "lifecycle:awaiting_signatures", title: "Awaiting Signatures"},
  {id: "awaiting-employee-signatures", value: "stage:awaiting_employee_signatures", title: "Awaiting Employee Signatures"},
  {id: "awaiting-family-signatures", value: "stage:awaiting_family_consent", title: "Awaiting Family Signatures"},
  {id: "ready-for-resident-presentation", value: "stage:ready_for_resident_presentation", title: "Ready for Resident Presentation"},
  {id: "awaiting-clinical-signature", value: "stage:awaiting_clinical_signature", title: "Awaiting GP / Clinician Signature"},
  {id: "completed", value: "lifecycle:completed", title: "Completed"},
  {id: "expired", value: "display:expired", title: "Expired"},
  {id: "review-overdue", value: "display:review_overdue", title: "Review Overdue"},
  {id: "declined", value: "lifecycle:declined", title: "Declined"},
  {id: "withdrawal-requested", value: "withdrawal:requested", title: "Withdrawal requested"},
  {id: "withdrawn", value: "lifecycle:withdrawn", title: "Withdrawn"},
  {id: "cancelled", value: "lifecycle:cancelled", title: "Cancelled"},
];

const reportStatusOptions: ISelectItem[] = [
  {id: ConsentDocumentDisplayStatus.DRAFT, value: ConsentDocumentDisplayStatus.DRAFT, title: "Draft"},
  {id: ConsentDocumentDisplayStatus.AWAITING_SIGNATURES, value: ConsentDocumentDisplayStatus.AWAITING_SIGNATURES, title: "Awaiting Signatures"},
  {id: ConsentDocumentDisplayStatus.COMPLETED, value: ConsentDocumentDisplayStatus.COMPLETED, title: "Completed"},
  {id: ConsentDocumentDisplayStatus.DECLINED, value: ConsentDocumentDisplayStatus.DECLINED, title: "Declined"},
  {id: ConsentDocumentDisplayStatus.WITHDRAWAL_REQUESTED, value: ConsentDocumentDisplayStatus.WITHDRAWAL_REQUESTED, title: "Withdrawal Requested"},
  {id: ConsentDocumentDisplayStatus.WITHDRAWN, value: ConsentDocumentDisplayStatus.WITHDRAWN, title: "Withdrawn"},
  {id: ConsentDocumentDisplayStatus.CANCELLED, value: ConsentDocumentDisplayStatus.CANCELLED, title: "Cancelled"},
  {id: ConsentDocumentDisplayStatus.EXPIRED, value: ConsentDocumentDisplayStatus.EXPIRED, title: "Expired"},
  {id: ConsentDocumentDisplayStatus.REVIEW_OVERDUE, value: ConsentDocumentDisplayStatus.REVIEW_OVERDUE, title: "Review Overdue"},
];

const asOption = (id: string, title: string): ISelectItem => ({id, value: id, title});

export const ConsentDocumentHistory = ({
  nursingHomeId,
  organizationId,
  residentId,
  canManage,
  showFilters = true,
}: {
  nursingHomeId: string;
  organizationId?: string;
  residentId?: string;
  canManage: boolean;
  showFilters?: boolean;
}) => {
  const [hiddenDraftIds, setHiddenDraftIds] = useState<string[]>([]);
  const [reportStatuses, setReportStatuses] = useState<string[]>([]);
  const [downloadResidentReport, reportLoading] = useFetching(async (statuses?: ConsentDocumentDisplayStatus[]) => {
    if (!residentId) return;
    try {
      const file = await ConsentController.downloadResidentReport(nursingHomeId, residentId, {statuses});
      saveConsentReportFile(file.bytes, file.fileName);
      return file;
    } catch (error) {
      notifyConsentReportError(error);
      throw error;
    }
  });
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Omit<ConsentHistoryFilters, "search" | "sort">>({statusFilter: "all"});
  const [categories, setCategories] = useState<ConsentCategory[]>([]);
  const [categoryError, setCategoryError] = useState("");
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchText), 500);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    if (!showFilters) return;

    let active = true;
    const loadOptions = async () => {
      setOptionsLoading(true);
      setCategoryError("");
      const requests: Promise<void>[] = [];
      if (organizationId) {
        requests.push(ConsentController.listCategories(organizationId, {page: 0, limit: 100, sort: "desc"})
          .then((items) => { if (active) setCategories(items); })
          .catch((error: unknown) => { if (active) setCategoryError(error instanceof Error ? error.message : "Unable to load Form Categories."); }));
      }
      await Promise.all(requests);
      if (active) setOptionsLoading(false);
    };
    loadOptions();
    return () => { active = false; };
  }, [organizationId, showFilters]);

  const query = useMemo(
    () => createConsentDocumentHistoryQuery(
      showFilters ? {...filters, search, residentId, sort: "desc"} : {residentId, sort: "desc"},
    ),
    [filters, residentId, search, showFilters],
  );
  const request = useMemo(
    () => (pageQuery: ConsentDocumentQuery & {page: number; limit: number}) =>
      ConsentController.listDocuments(nursingHomeId, pageQuery),
    [nursingHomeId],
  );
  const list = useIncrementalConsentList<ConsentDocument, ConsentDocumentQuery>(request, query);
  const categoryOptions = useMemo(
    () => [asOption("", "All Form Categories"), ...categories.map((category) => asOption(category._id, category.name))],
    [categories],
  );
  const hasFilters = showFilters && Boolean(
    searchText
    || (filters.statusFilter && filters.statusFilter !== "all")
    || filters.categoryId
    || filters.dateRange,
  );
  const clearFilters = () => {
    setSearchText("");
    setSearch("");
    setFilters({statusFilter: "all"});
  };
  const updateFilter = (key: "statusFilter" | "categoryId", event: SelectChangeEvent<string>) =>
    setFilters((current) => ({
      ...current,
      [key]: key === "statusFilter"
        ? event.target.value as ConsentHistoryStatusFilter
        : event.target.value || undefined,
    } as Omit<ConsentHistoryFilters, "search" | "sort">));

  const documents = list.items.filter((document) => !hiddenDraftIds.includes(document._id));
  const deleteDraft = async (document: ConsentDocument) => {
    try {
      await ConsentController.deleteDocument(nursingHomeId, document._id);
      setHiddenDraftIds((current) => current.includes(document._id) ? current : [...current, document._id]);
      new Notification().success("Draft deleted.");
    } catch (error: unknown) {
      new Notification().error(error instanceof Error ? error.message : "Unable to delete this draft.");
    }
  };
  const onDownloadResidentReport = () => {
    const statuses = reportStatuses.length && reportStatuses.length < reportStatusOptions.length
      ? reportStatuses as ConsentDocumentDisplayStatus[]
      : undefined;
    downloadResidentReport(statuses);
  };

  return <div className={styles.history}>
    {residentId && canDownloadConsentReports() ? <div className={styles.downloadBar}>
      <MultiSelectInput
        id="resident-consent-report-status"
        className={styles.statusSelect}
        value={reportStatuses}
        onChange={setReportStatuses}
        items={reportStatusOptions}
        placeholder="All statuses"
        showSelectAll
        selectAllLabel="All statuses"
      />
      <LoaderComponent isLoading={reportLoading} size={25}>
        <div className={styles.downloadAction} onClick={onDownloadResidentReport}>
          <Svg className={styles.downloadIcon} width={24} height={24} src={iconDownload} color="#333333"/>
          <span>Download Form report</span>
        </div>
      </LoaderComponent>
    </div> : null}
    {showFilters && <>
      <ConsentFilterBar onClear={hasFilters ? clearFilters : undefined}>
        <SearchInput id="consent-search" className={styles.search} value={searchText} onChange={(event) => setSearchText(event.target.value)} placeHolder="Search" fill={IFillVariant.Fill}/>
        <SelectInput id="consent-status" value={filters.statusFilter || "all"} handleChange={(event) => updateFilter("statusFilter", event)} items={statusOptions} placeholder="Status"/>
        <SelectInput id="consent-category" value={filters.categoryId || ""} handleChange={(event) => updateFilter("categoryId", event)} items={categoryOptions} placeholder={categoryError ? "Categories unavailable" : "Form Category"} disabled={Boolean(categoryError)}/>
        <DateRangePicker className={styles.dateRange} id="consent-date-range" value={filters.dateRange || null} onChange={(dateRange) => setFilters((current) => ({...current, dateRange}))} placeholder="Date range" maxDate={new Date()} fill={IFillVariant.Fill}/>
      </ConsentFilterBar>
      {(optionsLoading || categoryError) && <div className={styles.optionState}>
        {optionsLoading ? "Loading filter options…" : null}
        {categoryError ? `Form Categories unavailable: ${categoryError}` : null}
      </div>}
    </>}
    {list.isLoading && !list.items.length ? <ConsentRequestState variant="loading" title="Loading Forms"/> : null}
    {list.error && !list.items.length ? <ConsentRequestState variant="error" title="Unable to load Forms" description={list.error} onRetry={list.retry}/> : null}
    {!list.isLoading && !list.error && !documents.length ? <ConsentRequestState variant="empty" title={hasFilters ? "No matching forms" : residentId ? "No forms for this resident" : "No forms yet"} description={hasFilters ? "Try changing or clearing your filters." : "Forms will appear here when they are created."}/> : null}
    {documents.length ? <div className={styles.list}>{documents.map((document) => (
      <ConsentDocumentCard
        key={document._id}
        document={document}
        variant={document.status === ConsentDocumentStatus.DRAFT && canManage ? "editableDraft" : "default"}
        onDeleteDraft={document.status === ConsentDocumentStatus.DRAFT && canManage ? deleteDraft : undefined}
        link={document.status === ConsentDocumentStatus.DRAFT && canManage
          ? consentDocumentEditPath(document.resident._id, document._id)
          : consentDocumentDetailPath(document._id)}
      />
    ))}</div> : null}
    {list.error && list.items.length ? <ConsentRequestState variant="error" title="Unable to load more forms" description={list.error} onRetry={list.retry}/> : null}
    {list.items.length && !list.error ? <ConsentAutoLoad hasMore={list.hasMore} isLoading={list.isLoading} error={list.error} onLoadMore={list.loadMore}/> : null}
  </div>;
};

